import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import TemplateInput from "../common/form/TemplateInput";
import DocumentInput from "../common/form/DocumentInput";
import TriggerInput from "../common/form/TriggerInput";
import ProductInput from "../common/form/ProductInput";
import {
    useGetMaillingRuleDocumentsQuery,
    useGetMaillingRuleProductsQuery,
    useGetMaillingRuleTemplatesQuery,
    useGetOneMaillingRuleQuery,
    usePutMaillingRuleDocumentsMutation,
    usePutMaillingRuleProductsMutation,
    usePutMaillingRulesTemplatesMutation,
    usePutOneMaillingRuleMutation
} from "../../../../services/api/repositories/MaillingRuleRepository";

import moment from "moment";
import { IClientType } from "../../../../interfaces/api/IClientsType";
import ClientInput from "../common/form/ClientInput";

export default function MailingRuleEditSection() {

    const navigate = useNavigate();

    const { uuid } = useParams();

    const mailingRule = useGetOneMaillingRuleQuery(
        { uuid: uuid || "" },
        { refetchOnMountOrArgChange: true }
    );
    const templates = useGetMaillingRuleTemplatesQuery(
        { uuid: uuid || "" },
        { refetchOnMountOrArgChange: true }
    );
    const documents = useGetMaillingRuleDocumentsQuery(
        { uuid: uuid || "" },
        { refetchOnMountOrArgChange: true }
    );
    const products = useGetMaillingRuleProductsQuery(
        { uuid: uuid || "" },
        { refetchOnMountOrArgChange: true }
    );

    const [putOneMaillingRule, putOneMaillingRuleData] = usePutOneMaillingRuleMutation();
    //put template
    const [putMaillingRuleTemplate, putMaillingRuleTemplateData] = usePutMaillingRulesTemplatesMutation();
    //put document
    const [putMaillingRuleDocument, putMaillingRuleDocumentData] = usePutMaillingRuleDocumentsMutation();
    //put product
    const [putMaillingRuleProduct, putMaillingRuleProductData] = usePutMaillingRuleProductsMutation();

    const [affected, setAffected] = useState<{ basic: boolean, template: boolean, products: boolean, documents: boolean }>({ basic: false, template: false, products: false, documents: false });

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [trigger, setTrigger] = useState<string>("none");
    const [template, setTemplate] = useState<string>("none");

    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ name: string, description: string, client_types: string, trigger: string, template: string, dateEnd: string }>({ name: "", description: "", client_types: "", trigger: "", template: "", dateEnd: "" });
    const [isSetByUser, setIsSetByUser] = useState<{ name: boolean, description: boolean, client_types: boolean, trigger: boolean, template: boolean, dateEnd: boolean }>({ name: false, description: false, client_types: false, trigger: false, template: false, dateEnd: false });
    const [putIsCalled, setPutIsCalled] = useState<{ mailingRule: Boolean, templates: Boolean, documents: Boolean, products: Boolean }>({ mailingRule: false, templates: false, documents: false, products: false });

    const nameRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const statusRef = useRef<HTMLInputElement>(null);

    const [productList, setProductList] = useState<string[]>([]);

    const [documentUuidList, setDocumentUuidList] = useState<string[]>([]);

    const handleProductRemove = (value: string) => {
        setProductList(productList.filter(item => item !== value));
    }

    const handleProductAdd = (value: string) => {
        if (productList.includes(value)) {
            return;
        }
        setProductList([...productList, value]);
    }

    const handleDocumentUuidAdd = (uuid: string) => {
        if (documentUuidList.includes(uuid)) {
            return;
        }
        setDocumentUuidList([...documentUuidList, uuid]);
    }

    const handleDocumentUuidRemove = (uuid: string) => {
        setDocumentUuidList(documentUuidList.filter(item => item !== uuid));
    }

    const [clientTypeList, setClientTypeList] = useState<IClientType[]>([]);
    const [clientTypeUuidList, setclientTypeUuidList] = useState<string[]>([]);

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");

    const handleClientTypeAdd = (clientType: IClientType) => {
        if (clientTypeList.some(existingClientType => existingClientType.uuid === clientType.uuid)) {
            return;
        }
        setClientTypeList([...clientTypeList, clientType]);
    };

    const handleClientTypeRemove = (value: string) => {
        setClientTypeList(clientTypeList.filter(item => item.uuid !== value));
        setclientTypeUuidList(clientTypeUuidList.filter(item => item !== value));
    }

    const handleClientTypeUuidAdd = (uuid: string) => {
        if (clientTypeUuidList.some(existingUuidClient => existingUuidClient === uuid)) {
            return;
        }
        setclientTypeUuidList([...clientTypeUuidList, uuid]);
    };

    const handleClose = useCallback(() => {
        setShow(false)
    }, [setShow]);

    const handleShow = useCallback(async () => {

        setShowValidation(true);

        let errors = validationErrors;
        let canSend = true;

        if (nameRef.current?.value === '') {
            errors.name = "Le nom est obligatoire";
            setIsSetByUser(prevState => ({ ...prevState, name: true }));
            canSend = false;
        }

        if (descriptionRef.current?.value.length === 0) {
            errors.description = "Veuillez saisir une description";
            setIsSetByUser(prevState => ({ ...prevState, description: true }));
            canSend = false;
        }

        if (descriptionRef.current?.value && descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            setIsSetByUser(prevState => ({ ...prevState, description: true }));
            canSend = false;
        }

        if (clientTypeList.length === 0) {
            errors.client_types = "Veuillez sélectionner un client";
            setIsSetByUser(prevState => ({ ...prevState, client_types: true }));
            canSend = false;
        }

        if (startDate && endDate && startDate !== null && endDate !== null && startDate > endDate) {
            errors.dateEnd = "La date de fin doit être postérieure à la date de début";
            setIsSetByUser(prevState => ({ ...prevState, dateEnd: true }));
            canSend = false;
        }

        let start_date = null;

        if (startDate && startDate !== null) {
            start_date = `${startDate?.toLocaleDateString()} 00:00:00`;
        } else {
            start_date = `${new Date().toLocaleDateString()} 00:00:00`;
        }

        let end_date = null;
        if (endDate && endDate !== null) {
            end_date = `${endDate.toLocaleDateString()} 23:59:59`;
        }

        if (clientTypeList.length === 0 && !isSetByUser.client_types) {
            errors.client_types = "Veuillez sélectionner au minimum un client cible";
            setIsSetByUser(prevState => ({ ...prevState, client_types: true }));
            canSend = false;
        }

        if (trigger === "none" && !isSetByUser.trigger) {
            errors.trigger = "Veuillez sélectionner un déclencheur";
            setIsSetByUser(prevState => ({ ...prevState, trigger: true }));
            canSend = false;
        }

        if (template === "none" && !isSetByUser.template) {
            errors.template = "Veuillez sélectionner un template";
            setIsSetByUser(prevState => ({ ...prevState, template: true }));
            canSend = false;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        setShow(true)
    }, [validationErrors, clientTypeList.length, startDate, endDate, isSetByUser.client_types, isSetByUser.trigger, isSetByUser.template, trigger, template]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            setValidationErrors({ ...validationErrors, name: "" });
        }
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;

        if (value.length > 0) {
            setValidationErrors({ ...validationErrors, description: "" });
        }
    }

    const handleSubmit = useCallback(async () => {

        if (uuid === undefined || clientTypeList.length === 0) {
            return;
        }

        let start_date = null;

        if (startDate && startDate !== null && startDate !== undefined) {
            start_date = `${startDate.toLocaleDateString()} 00:00:00`;
        } else {
            start_date = `${new Date().toLocaleDateString()} 00:00:00`;
        }

        let end_date = null;
        if (endDate && endDate !== null && endDate !== undefined) {
            end_date = `${endDate.toLocaleDateString()} 23:59:59`;
        }
        let status = 'inactive';
        if (statusRef.current && statusRef.current.checked) {
            status = 'active';
        }

        putOneMaillingRule({
            uuid: uuid,
            data:
            {
                name: (nameRef.current ? nameRef.current.value : ''),
                description: (descriptionRef.current ? descriptionRef.current.value : ''),
                client_types: clientTypeUuidList,
                status: status,
                start_date: start_date,
                end_date: end_date,
                trigger_uuid: (trigger !== "none" ? trigger : ''),
            }
        }
        );

        setPutIsCalled({ ...putIsCalled, mailingRule: true });

        if (template !== "none" && templates.data && "templates" in templates.data && template !== templates.data.templates[0].uuid) {
            putMaillingRuleTemplate({ uuid: uuid, data: { template_uuid: template } });
            setPutIsCalled({ ...putIsCalled, templates: true });
        }

        if (products.data && products.data !== undefined && products.data && "products" in products.data) {
            putMaillingRuleProduct({ uuid: uuid, data: { products: productList.map(product => ({ reference: product })) } });
            setPutIsCalled({ ...putIsCalled, products: true });
        }

        if (documents.data && documents.data !== undefined && documents.data && "documents" in documents.data) {
            putMaillingRuleDocument({ uuid: uuid, data: { documents: documentUuidList.map(document => ({ uuid: document })) } });
            setPutIsCalled({ ...putIsCalled, documents: true });
        }

        setShow(false);
    }, [uuid, clientTypeList.length, startDate, endDate, putOneMaillingRule, clientTypeUuidList, trigger, putIsCalled, template, templates.data, products.data, documents.data, putMaillingRuleTemplate, putMaillingRuleProduct, productList, putMaillingRuleDocument, documentUuidList]);

    const handleError = useCallback(() => {
        if (putOneMaillingRuleData.isError &&
            putOneMaillingRuleData.error !== undefined &&
            putOneMaillingRuleData.error !== null
            && typeof putOneMaillingRuleData.error === 'object'
            && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {

            if ("error" in putOneMaillingRuleData.error) {
                setErrMsg(putOneMaillingRuleData.error.error as string);
            } else if ("data" in putOneMaillingRuleData.error && typeof putOneMaillingRuleData.error.data === 'object' && putOneMaillingRuleData.error.data !== null && "message" in putOneMaillingRuleData.error.data) {
                setErrMsg(putOneMaillingRuleData.error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }

        if (putMaillingRuleTemplateData.isError &&
            putMaillingRuleTemplateData.error !== undefined &&
            putMaillingRuleTemplateData.error !== null
            && typeof putMaillingRuleTemplateData.error === 'object'
            && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {

            if ("error" in putMaillingRuleTemplateData.error) {
                setErrMsg(putMaillingRuleTemplateData.error.error as string);
            } else if ("data" in putMaillingRuleTemplateData.error && typeof putMaillingRuleTemplateData.error.data === 'object' && putMaillingRuleTemplateData.error.data !== null && "message" in putMaillingRuleTemplateData.error.data) {
                setErrMsg(putMaillingRuleTemplateData.error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }

        if (putMaillingRuleDocumentData.isError &&
            putMaillingRuleDocumentData.error !== undefined &&
            putMaillingRuleDocumentData.error !== null
            && typeof putMaillingRuleDocumentData.error === 'object'
            && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {

            if ("error" in putMaillingRuleDocumentData.error) {
                setErrMsg(putMaillingRuleDocumentData.error.error as string);
            } else if ("data" in putMaillingRuleDocumentData.error && typeof putMaillingRuleDocumentData.error.data === 'object' && putMaillingRuleDocumentData.error.data !== null && "message" in putMaillingRuleDocumentData.error.data) {
                setErrMsg(putMaillingRuleDocumentData.error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }

        if (putMaillingRuleProductData.isError &&
            putMaillingRuleProductData.error !== undefined &&
            putMaillingRuleProductData.error !== null
            && typeof putMaillingRuleProductData.error === 'object'
            && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {

            if ("error" in putMaillingRuleProductData.error) {
                setErrMsg(putMaillingRuleProductData.error.error as string);
            } else if ("data" in putMaillingRuleProductData.error && typeof putMaillingRuleProductData.error.data === 'object' && putMaillingRuleProductData.error.data !== null && "message" in putMaillingRuleProductData.error.data) {
                setErrMsg(putMaillingRuleProductData.error.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [putOneMaillingRuleData.isError, putOneMaillingRuleData.error,
        errMsg, putMaillingRuleTemplateData.isError,
    putMaillingRuleTemplateData.error, putMaillingRuleDocumentData.isError,
    putMaillingRuleDocumentData.error, putMaillingRuleProductData.isError,
    putMaillingRuleProductData.error
    ]);

    useEffect(() => {
        //affect basic values to refs and states
        if (mailingRule.data && 'uuid' in mailingRule.data
            && nameRef.current && descriptionRef.current && statusRef.current
            && !affected.basic
        ) {
            const start_date = mailingRule.data.start_date;
            const end_date = mailingRule.data.end_date;
            nameRef.current!.value = mailingRule.data.name;
            descriptionRef.current!.value = mailingRule.data.description;
            if (mailingRule.data.status === "active") {
                statusRef.current!.checked = true;
            } else {
                statusRef.current!.checked = false;
            }
            setClientTypeList(mailingRule.data.client_types);
            setclientTypeUuidList(mailingRule.data.client_types.map(clientType => clientType.uuid));
            setTrigger(mailingRule.data.trigger_uuid);
            setStartDate(moment(start_date, "DD/MM/YYYY").toDate());
            if (end_date) {
                setEndDate(moment(end_date, "DD/MM/YYYY").toDate());
            }
            setAffected({ ...affected, basic: true });
        }

        //affect template value
        if (templates.data && "templates" in templates.data && templates.data.templates.length > 0 && !affected.template) {
            setTemplate(templates.data.templates[0].uuid);
            setAffected({ ...affected, template: true });
        }

        //affect products value
        if (products.data && "products" in products.data && products.data.products.length > 0 && !affected.products) {
            setProductList(products.data.products.map(product => product.reference));
            setAffected({ ...affected, products: true });
        }

        //affect documents value
        if (documents.data && "documents" in documents.data && documents.data.documents.length > 0 && !affected.documents) {
            setDocumentUuidList(documents.data.documents.map(document => document.uuid));
            setAffected({ ...affected, documents: true });
        }

        if (
            (putOneMaillingRuleData.isSuccess) &&
            (putMaillingRuleTemplateData.isSuccess || !putIsCalled.templates) &&
            (putMaillingRuleProductData.isSuccess || !putIsCalled.products) &&
            (putMaillingRuleDocumentData.isSuccess || !putIsCalled.documents)
        ) {
            navigate(`/mailing-rules/${uuid}`);
        }

        if (putOneMaillingRuleData.isError || putMaillingRuleTemplateData.isError || putMaillingRuleProductData.isError || putMaillingRuleDocumentData.isError) {
            handleError();
        }

    }, [mailingRule, templates, documents,
        products, affected, putOneMaillingRuleData.isSuccess,
        putMaillingRuleTemplateData.isSuccess, putMaillingRuleProductData.isSuccess,
        putMaillingRuleDocumentData.isSuccess, uuid, putOneMaillingRuleData.isLoading,
        putOneMaillingRuleData.data, putMaillingRuleTemplateData.isLoading,
        putMaillingRuleProductData.isLoading, putMaillingRuleDocumentData.isLoading,
        navigate, putIsCalled.templates, putIsCalled.products, putIsCalled.documents,
        putOneMaillingRuleData.isError, putMaillingRuleTemplateData.isError,
        putMaillingRuleProductData.isError, putMaillingRuleDocumentData.isError, handleError
    ]);

    return (
        <>
            {(mailingRule.isLoading || templates.isLoading || documents.isLoading || products.isLoading)
                && <div>Chargement...</div>}

            {(mailingRule.error as ReactNode || templates.error as ReactNode || documents.error as ReactNode || products.error as ReactNode) && (
                <div>Erreur lors du Chargement</div>
            )}

            {(!mailingRule.isLoading && !templates.isLoading && !documents.isLoading && !products.isLoading
                && mailingRule.data && templates.data && documents.data && products.data
            ) && (
                    <>
                        <div className="object mailing" data-status="active">
                            <header>
                                <h2>{"uuid" in mailingRule.data && mailingRule.data.name}</h2>
                                <div className="object-date" title="">
                                    <span className="icon"></span>
                                    <span className="text">{/**"updated_at" in mailingRule.data && mailingRule.data.updated_at**/}</span>
                                </div>
                            </header>
                            <Form className="form" validated={showValidation}>
                                <section className="form-section id">
                                    <h3>
                                        <span className="icon"></span>
                                        <span className="text">Définition</span>
                                    </h3>
                                    <Form.Group className="mailing-name required">
                                        <Form.Label className="form-label" htmlFor="mailing-name-input">Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="form-control w100"
                                            id="mailing-name-input"
                                            tabIndex={1}
                                            ref={nameRef}
                                            onChange={handleNameChange}
                                            isInvalid={validationErrors.name.length > 0}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.name}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mailing-description">
                                        <Form.Label className="form-label" htmlFor="mailing-description-input">Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            type="text"
                                            className="form-control w100"
                                            id="mailing-description-input"
                                            tabIndex={2}
                                            ref={descriptionRef}
                                            onChange={handleDescriptionChange}
                                            isInvalid={validationErrors.description.length > 0}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.description}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group className="mailing-status required">
                                        <Form.Label className="form=label" htmlFor="mailing-status-checkbox">Statut</Form.Label>
                                        <div className="form-switch status">
                                            <Form.Control
                                                type="checkbox"
                                                className="form-switch-checkbox"
                                                id="mailing-status-checkbox"
                                                tabIndex={3}
                                                ref={statusRef}
                                            />
                                            <Form.Label className="form-switch-label" htmlFor="mailing-status-checkbox">
                                                <span className="icon"></span>
                                                <span className="text">
                                                    <span className="on">Actif</span>
                                                    <span className="off">Inactif</span>
                                                </span>
                                            </Form.Label>
                                        </div>
                                    </Form.Group>

                                    <ClientInput
                                        clientTypeList={clientTypeList}
                                        handleClientTypeAdd={handleClientTypeAdd}
                                        handleClientTypeUuidAdd={handleClientTypeUuidAdd}
                                        handleClientTypeRemove={handleClientTypeRemove}
                                    />
                                </section>

                                <section className="form-section schedule">
                                    <h3>
                                        <span className="icon"></span>
                                        <span className="text">Programmation</span>
                                    </h3>
                                    <div className="fromTo">
                                        <Form.Group className="mailing-runDateStart">
                                            <Form.Label className="form-label" htmlFor="mailing-runDateStart-input">Du</Form.Label>
                                            <DatePicker className="form-control w100" id="mailing-runDateStart-input" tabIndex={4}
                                                dateFormat="dd/MM/yyyy"
                                                selected={startDate}
                                                onChange={(date) => { setStartDate(date) }} />
                                        </Form.Group>
                                        <Form.Group className="mailing-runDateEnd">
                                            <Form.Label className="form-label" htmlFor="mailing-runDateEnd-input">Au</Form.Label>
                                            <DatePicker className="form-control w100" id="mailing-runDateEnd-input" tabIndex={5}
                                                dateFormat="dd/MM/yyyy"
                                                selected={endDate}
                                                onChange={(date) => { setEndDate(date) }} />
                                        </Form.Group>
                                    </div>
                                </section>
                                <section className="form-section trigger">
                                    <h3>
                                        <span className="icon"></span>
                                        <span className="text">Déclenchement</span>
                                    </h3>
                                    <TriggerInput
                                        tabIndex={6}
                                        setTrigger={setTrigger}
                                        trigger={trigger}
                                        triggerValidationError={validationErrors.trigger}
                                        triggerIsSetByUser={isSetByUser.trigger}
                                        setTriggerIsSetByUser={(triggerIsSetByUser: boolean) => setIsSetByUser(prevState => ({ ...prevState, trigger: triggerIsSetByUser }))}
                                    />
                                    <ProductInput productList={productList} handleProductAdd={handleProductAdd} handleProductRemove={handleProductRemove} />
                                </section>
                                <section className="form-section email">
                                    <h3>
                                        <span className="icon"></span>
                                        <span className="text">Email</span>
                                    </h3>
                                    <TemplateInput
                                        tabIndex={9}
                                        setTemplate={setTemplate}
                                        template={template}
                                        templateValidationError={validationErrors.template}
                                        templateIsSetByUser={isSetByUser.template}
                                        setTemplateIsSetByUser={(templateIsSetByUser: boolean) => setIsSetByUser(prevState => ({ ...prevState, template: templateIsSetByUser }))}
                                    />
                                    <DocumentInput
                                        documentUuidList={documentUuidList}
                                        handleDocumentUuidAdd={handleDocumentUuidAdd}
                                        handleDocumentUuidRemove={handleDocumentUuidRemove}
                                    />
                                </section>

                                {(validationErrors.dateEnd) && (
                                    <Alert
                                        variant="danger"
                                        dismissible>
                                        {validationErrors.dateEnd}
                                    </Alert>
                                )}

                                {(validationErrors.client_types) && (
                                    <Alert
                                        variant="danger"
                                        dismissible>
                                        {validationErrors.client_types}
                                    </Alert>
                                )}

                                {(errMsg.length > 0) && (
                                    <>
                                        <Alert
                                            variant="danger"
                                            dismissible >
                                            {errMsg}
                                        </Alert>
                                    </>
                                )}

                                <div className="form-submit">
                                    <NavLink to={`/mailing-rules/${uuid}`} className="btn btn-secondary btn-cancel" onClick={() => { }} tabIndex={13} title="Annuler">
                                        <span className="icon"></span>
                                        <span className="text">Annuler</span>
                                    </NavLink>
                                    <Button
                                        type="button"
                                        className="btn btn-primary btn-save"
                                        data-bs-toggle="modal"
                                        data-bs-target="#mailingCreateConfirmationModal"
                                        tabIndex={12}
                                        onClick={() => { handleShow() }}
                                        title="Enregistrer les modifications de la configuration d'envoi">
                                        <span className="icon"></span>
                                        <span className="text">Enregistrer</span>
                                    </Button>
                                </div>
                            </Form>
                        </div>
                        <SubmitModal
                            show={show}
                            handleClose={() => { handleClose() }}
                            handleSubmit={() => { handleSubmit() }}
                            name={nameRef.current?.value || ""}
                            status={statusRef.current?.value || ""}
                            startDate={startDate?.toLocaleDateString() || null}
                            endDate={endDate?.toLocaleDateString() || null} />
                    </>
                )}
        </>
    );
}


function SubmitModal(
    { show, handleClose, handleSubmit, name, status, startDate, endDate }:
        { show: boolean, handleClose: () => void, handleSubmit: () => void, name: string, status: string, startDate: string | null, endDate: string | null }) {

    return (
        <Modal id="mailingSaveConfirmationModal" show={show} onHide={() => { handleClose() }} s>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="mailingSaveConfirmationModal">
                    <span className="icon"></span>
                    <span className="text">Modification d'une configuration d'envoi</span>
                </h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez enregistrer vos modifications à la configuration d'envoi&nbsp;:</p>
                <div className="object mailing" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                    <div className="object-status" data-status="active">
                        <span className="icon"></span>
                        <span className="text">{status}</span>
                    </div>
                    <p>En exploitation à partir du {(startDate !== undefined && startDate !== null) && (<>{startDate}</>)} {(endDate !== undefined && endDate !== null) && (<>jusqu'au {endDate}</>)}</p>
                </div>
                <p>Veuillez valider les modifications.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className="btn btn-secondary btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={() => { handleClose() }}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    className="btn btn-primary btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={() => { handleSubmit() }}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}